/**
 * The inbox: what has arrived at this desk and is still actionable.
 *
 * Every card shows its state — reading, needs review, ready, failed — with a
 * live progress bar while OCR runs. In the legacy system this stage was
 * invisible: the browser simply stopped responding for several seconds and
 * receptionists learned to click twice.
 *
 * One kind of card is not a document yet: a print from a PC that no desk has
 * claimed. There is one virtual printer now and the desk is decided by the
 * machine, so the very first print from a new PC has nowhere to go until a
 * person says "that is mine". It sits at the top of the inbox, named by its
 * machine, with a single button — and pressing it binds the PC to this desk so
 * the question is never asked again.
 *
 * Two ways to empty it, kept separate on purpose. Failures are the one card
 * nothing ever consumes — worth reading once and worth removing immediately
 * afterwards — so clearing those is offered whenever there are any. Clearing
 * everything is offered too, but it takes documents that are perfectly good,
 * which is why it is its own button with its own wording rather than a
 * checkbox on the first one.
 *
 * It folds away to a rail. The document is what the desk is for, and a
 * 300px list beside it was a third of the width a registration card could
 * have been read at. Folded, it still says how many documents are waiting and
 * whether one needs a person, so nothing arrives unseen.
 */

import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  FileUp,
  Inbox,
  Loader2,
  MonitorSmartphone,
  PanelLeftClose,
  PanelLeftOpen,
  Trash2,
} from 'lucide-react'
import type { CaptureJob } from '@/lib/types'
import { Badge, Button, Card, Progress } from '@/components/ui/primitives'
import { cn, formatRelative } from '@/lib/utils'

const STAGE_LABELS: Record<string, string> = {
  rasterising: 'Rendering pages',
  detecting: 'Identifying the document',
  extracting: 'Reading fields',
  done: 'Ready',
  failed: 'Failed',
}

export function InboxPanel({
  jobs,
  selectedId,
  onSelect,
  onAdopt,
  adoptingId,
  onClear,
  clearing,
  disabled,
  onCollapse,
  onUpload,
}: {
  jobs: CaptureJob[]
  selectedId: string | null
  onSelect: (id: string) => void
  /** Take an unassigned print onto this desk, binding the PC that sent it. */
  onAdopt?: (id: string) => void
  adoptingId?: string | null
  /**
   * Ask to empty the inbox. Asks — it does not do it: both scopes are
   * irreversible and the confirmation belongs to whoever owns the mutation.
   */
  onClear?: (scope: 'failed' | 'all') => void
  clearing?: 'failed' | 'all' | null
  disabled?: boolean
  /** Fold the inbox to its rail. */
  onCollapse?: () => void
  onUpload?: () => void
}) {
  // Unassigned first and always: it is the only card that cannot act on
  // itself, and a guest is standing at whichever desk that PC belongs to.
  const waiting = jobs.filter((job) => !job.desk_id)
  const mine = jobs.filter((job) => job.desk_id)
  const failed = mine.filter((job) => job.status === 'failed')

  return (
    <div className="flex flex-col gap-2">
      {/* One row, and it stays put while the cards scroll under it: the clear
          buttons used to sit on a line of their own below the count. */}
      <div className="sticky top-0 z-10 flex h-7 items-center gap-1 bg-[var(--bg)] px-1">
        {onCollapse && (
          <Button
            variant="ghost"
            size="icon"
            className="-ms-1 h-6 w-6 text-[var(--text-muted)]"
            aria-label="Hide the inbox"
            title="Hide the inbox"
            icon={<PanelLeftClose className="h-3.5 w-3.5" />}
            onClick={onCollapse}
          />
        )}
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
          Inbox
        </h2>
        <span className="tabular me-auto text-[11px] text-[var(--text-muted)]">{jobs.length}</span>

        {onUpload && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-[var(--text-muted)]"
            aria-label="Upload a PDF"
            title="Upload a PDF"
            icon={<FileUp className="h-3.5 w-3.5" />}
            onClick={onUpload}
          />
        )}

        {/* Two scopes, never one button that means both. Clearing the failures
          throws away nothing anybody wants; clearing everything throws away
          documents a guest may be standing there waiting for, and the two
          should not be one click apart from each other by accident. Each
          confirms before anything goes. */}
        {onClear && mine.length > 0 && (
          <>
            {failed.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1.5 px-2 text-[11px] text-[var(--text-muted)]"
                icon={<Trash2 className="h-3 w-3" />}
                loading={clearing === 'failed'}
                disabled={Boolean(clearing)}
                onClick={() => onClear('failed')}
              >
                Clear {failed.length} failed
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1.5 px-2 text-[11px] text-[var(--text-muted)]"
              icon={<Trash2 className="h-3 w-3" />}
              loading={clearing === 'all'}
              disabled={Boolean(clearing)}
              onClick={() => onClear('all')}
            >
              Clear all
            </Button>
          </>
        )}
      </div>

      {waiting.length > 0 && (
        <ul className="flex flex-col gap-2">
          {waiting.map((job) => (
            <li key={job.id}>
              <UnassignedCard
                job={job}
                busy={adoptingId === job.id}
                onAdopt={onAdopt ? () => onAdopt(job.id) : undefined}
              />
            </li>
          ))}
        </ul>
      )}

      {jobs.length === 0 ? (
        <Card className="px-4 py-8 text-center">
          <FileText className="mx-auto h-5 w-5 text-[var(--text-muted)]" aria-hidden />
          <p className="mt-2 text-[13px] text-[var(--text-muted)]">
            Nothing waiting. Print from your PMS to begin.
          </p>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {mine.map((job) => (
            <li key={job.id}>
              <JobCard
                job={job}
                selected={job.id === selectedId}
                disabled={disabled}
                onSelect={() => onSelect(job.id)}
              />
            </li>
          ))}
        </ul>
      )}

      {disabled && jobs.length > 0 && (
        <p className="px-1 text-[12px] text-[var(--text-muted)]">
          A guest is signing right now. Finish or cancel that session before starting another
          document.
        </p>
      )}
    </div>
  )
}

/**
 * The inbox folded away: a strip down the edge that still counts what is
 * waiting, flags what needs a person, and opens again with one click.
 */
export function InboxRail({
  jobs,
  onExpand,
  onUpload,
}: {
  jobs: CaptureJob[]
  onExpand: () => void
  onUpload?: () => void
}) {
  // Worth a colour on the rail: a print nobody has claimed, a document the
  // scanner was unsure of, or one it could not read at all.
  const unclaimed = jobs.filter((job) => !job.desk_id).length
  const needsYou = jobs.filter(
    (job) => job.desk_id && (job.status === 'review_required' || job.status === 'failed'),
  ).length

  return (
    <div className="flex flex-col items-center gap-1 rounded-[12px] border border-[var(--border)] bg-[var(--surface)] py-1.5 xl:h-full">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-[var(--text-muted)]"
        aria-label="Show the inbox"
        title="Show the inbox"
        icon={<PanelLeftOpen className="h-4 w-4" />}
        onClick={onExpand}
      />
      <button
        type="button"
        onClick={onExpand}
        title={`Inbox: ${jobs.length} document${jobs.length === 1 ? '' : 's'}`}
        aria-label={`Show the inbox, ${jobs.length} document${jobs.length === 1 ? '' : 's'}`}
        className="flex w-8 flex-col items-center gap-0.5 rounded-[8px] py-1.5 text-[var(--text-muted)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
      >
        <Inbox className="h-4 w-4" aria-hidden />
        <span className="tabular text-[11px] font-medium">{jobs.length}</span>
      </button>
      {unclaimed > 0 && (
        <span
          className="tabular grid h-5 min-w-5 place-items-center rounded-full bg-[var(--info-subtle)] px-1 text-[10px] font-semibold text-[var(--info)]"
          title={`${unclaimed} print${unclaimed === 1 ? '' : 's'} from a PC no desk has claimed`}
        >
          {unclaimed}
        </span>
      )}
      {needsYou > 0 && (
        <span
          className="tabular grid h-5 min-w-5 place-items-center rounded-full bg-[var(--warning-subtle)] px-1 text-[10px] font-semibold text-[var(--warning)]"
          title={`${needsYou} document${needsYou === 1 ? '' : 's'} to check`}
        >
          {needsYou}
        </span>
      )}
      {onUpload && (
        <Button
          variant="ghost"
          size="icon"
          className="mt-1 h-8 w-8 text-[var(--text-muted)]"
          aria-label="Upload a PDF"
          title="Upload a PDF"
          icon={<FileUp className="h-4 w-4" />}
          onClick={onUpload}
        />
      )}
    </div>
  )
}

/**
 * A print whose PC is not bound to a desk yet.
 *
 * Deliberately not selectable: there is nothing to review until it belongs
 * somewhere, and offering the document itself would invite a receptionist to
 * start working on a colleague's guest. One button, one decision — is this
 * machine yours — and the machine name is there to answer it with.
 */
function UnassignedCard({
  job,
  busy,
  onAdopt,
}: {
  job: CaptureJob
  busy?: boolean
  onAdopt?: () => void
}) {
  return (
    <Card className="border-[var(--info)] bg-[var(--info-subtle)] p-3">
      <div className="flex items-start gap-2">
        <MonitorSmartphone className="mt-0.5 h-4 w-4 shrink-0 text-[var(--info)]" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium">
            {job.original_filename ?? 'A document'} was printed
          </p>
          <p className="mt-0.5 break-all text-[12px] text-[var(--text-muted)]">
            from <span className="font-medium">{job.workstation ?? 'an unknown PC'}</span> ·{' '}
            {formatRelative(job.created_at)}
          </p>
        </div>
      </div>

      <Button
        variant="primary"
        size="sm"
        className="mt-2.5 w-full"
        loading={busy}
        disabled={!onAdopt}
        onClick={onAdopt}
      >
        Send it to my desk
      </Button>
      <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">
        This also binds that PC to this desk, so its next print arrives here on its own.
      </p>
    </Card>
  )
}

function JobCard({
  job,
  selected,
  disabled,
  onSelect,
}: {
  job: CaptureJob
  selected: boolean
  disabled?: boolean
  onSelect: () => void
}) {
  const busy = job.status === 'queued' || job.status === 'processing'
  const failed = job.status === 'failed'
  const needsReview = job.status === 'review_required'

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled || busy}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'w-full rounded-[12px] border bg-[var(--surface)] p-3 text-start transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-70',
        selected
          ? 'border-[var(--brand)] ring-1 ring-[var(--brand)]'
          : 'border-[var(--border)] hover:border-[var(--text-muted)]',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="truncate text-[13px] font-medium">
          {job.document_type_label ?? job.original_filename ?? 'Unidentified document'}
        </span>
        {failed ? (
          <AlertTriangle className="h-4 w-4 shrink-0 text-[var(--danger)]" aria-hidden />
        ) : busy ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--brand)]" aria-hidden />
        ) : (
          <CheckCircle2
            className={cn(
              'h-4 w-4 shrink-0',
              needsReview ? 'text-[var(--warning)]' : 'text-[var(--success)]',
            )}
            aria-hidden
          />
        )}
      </div>

      {busy ? (
        <div className="mt-2 flex flex-col gap-1.5">
          <span className="text-[12px] text-[var(--text-muted)]">
            {STAGE_LABELS[job.progress_stage ?? ''] ?? 'Working'} · {job.progress_percent}%
          </span>
          <Progress value={job.progress_percent} label="Reading the document" />
        </div>
      ) : failed ? (
        <p className="mt-1.5 line-clamp-2 text-[12px] text-[var(--danger)]">{job.error_message}</p>
      ) : (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {needsReview && (
            <Badge tone="warning">
              {job.fields_needing_review} field{job.fields_needing_review === 1 ? '' : 's'} to check
            </Badge>
          )}
          {job.detection_score != null && job.detection_score < 0.8 && (
            <Badge tone="neutral">match {Math.round(job.detection_score * 100)}%</Badge>
          )}
          <span className="text-[12px] text-[var(--text-muted)]">
            {formatRelative(job.created_at)}
          </span>
        </div>
      )}
    </button>
  )
}
